import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { StarMark } from "@/components/Logo";
import { Seo } from "@/components/Seo";
import { Mermaid } from "@/components/Mermaid";
import { LeadFormDialog } from "@/components/LeadFormDialog";
import { LocaleSwitcher } from "@/components/LocaleSwitcher";
import { useAuth } from "@/context/AuthContext";
import {
  Radar, Globe2, ShieldCheck, Network, Banknote, Scale, Leaf, Activity,
  ArrowRight, Download, Sparkles, Lock, CheckCircle2, Accessibility,
} from "lucide-react";

const FLOW = `flowchart LR
  A["Broad live signals<br/>geopolitical · cyber<br/>supplier · financial · ESG"] --> B["Normalise<br/>& correlate"]
  B --> C["Enterprise<br/>risk graph"]
  C --> D["Quantify<br/>£ exposure"]
  C --> E["Foresight<br/>& pre-mortem"]
  D --> F["Board-ready<br/>decisions"]
  E --> F
  F --> G["Human-approved<br/>action"]
  G --> H["Immutable<br/>audit trail"]`;

// Slug→display name mapping for live-integrations strips. Module-level constant
// so it isn't reallocated on every render (also satisfies react-hooks/purity).
const SLUG_LABEL = { servicenow: "ServiceNow", jira: "Jira",
                     "ms-project": "MS Project", smartsheet: "Smartsheet" };

// Pure formatter — given a minute count returns a humanised "x ago" string.
// Kept outside components so it's referentially stable and react-hooks/purity-safe.
function formatAgo(mins) {
  if (mins == null) return "";
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  if (mins < 24 * 60) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / (24 * 60))}d ago`;
}

/**
 * useMinutesSince — React hook that returns the integer minute-count since
 * `iso` and ticks once a minute. Keeps `Date.now()` impurity out of render
 * by deriving from a ticking `nowTs` state (lazy-init + interval update).
 */
function useMinutesSince(iso) {
  const [nowTs, setNowTs] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowTs(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);
  if (!iso) return null;
  return Math.max(0, Math.round((nowTs - new Date(iso).getTime()) / 60_000));
}

export default function HowItWorks() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAuthed = user && typeof user === "object";

  const SIGNAL_FAMILIES = [
    { icon: Globe2, title: t("howItWorks.signalGeoTitle"), desc: t("howItWorks.signalGeoDesc") },
    { icon: Network, title: t("howItWorks.signalSupplyTitle"), desc: t("howItWorks.signalSupplyDesc") },
    { icon: ShieldCheck, title: t("howItWorks.signalSanctionsTitle"), desc: t("howItWorks.signalSanctionsDesc") },
    { icon: Radar, title: t("howItWorks.signalCyberTitle"), desc: t("howItWorks.signalCyberDesc") },
    { icon: Scale, title: t("howItWorks.signalRegTitle"), desc: t("howItWorks.signalRegDesc") },
    { icon: Banknote, title: t("howItWorks.signalFinTitle"), desc: t("howItWorks.signalFinDesc") },
    { icon: Leaf, title: t("howItWorks.signalEsgTitle"), desc: t("howItWorks.signalEsgDesc") },
    { icon: Activity, title: t("howItWorks.signalOpsTitle"), desc: t("howItWorks.signalOpsDesc") },
  ];

  const OUTCOMES = [
    { stat: t("howItWorks.outcomeHeatTo"), to: t("howItWorks.outcomeHeatLabel"), desc: t("howItWorks.outcomeHeatDesc") },
    { stat: t("howItWorks.outcomeSnapTo"), to: t("howItWorks.outcomeSnapLabel"), desc: t("howItWorks.outcomeSnapDesc") },
    { stat: t("howItWorks.outcomeHindTo"), to: t("howItWorks.outcomeHindLabel"), desc: t("howItWorks.outcomeHindDesc") },
    { stat: t("howItWorks.outcomeReportTo"), to: t("howItWorks.outcomeReportLabel"), desc: t("howItWorks.outcomeReportDesc") },
  ];

  const TRUST = [
    { icon: CheckCircle2, label: t("howItWorks.trustDeterministic"), desc: t("howItWorks.trustDeterministicDesc") },
    { icon: Lock, label: t("howItWorks.trustAuditFirst"), desc: t("howItWorks.trustAuditFirstDesc") },
    { icon: ShieldCheck, label: t("howItWorks.trustPatentLabel"), desc: t("howItWorks.trustPatentDesc") },
  ];

  return (
    <div className="min-h-screen overflow-x-hidden bg-ek-canvas">
      <Seo title={t("howItWorks.seoTitle")} description={t("howItWorks.seoDescription")} path="/how-it-works" localized />

      {/* Public header */}
      <header className="sticky top-0 z-40 border-b border-white/10 bg-black">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6">
          <Link to="/" className="flex items-center gap-2.5" data-testid="brochure-home-link">
            <StarMark size={26} className="text-ek-lilac" />
            <span className="font-display text-lg font-bold text-white">EKSELENS <span className="text-ek-lilac">· ERIO</span></span>
          </Link>
          <div className="flex items-center gap-3">
            <div className="hidden sm:inline-flex">
              <LocaleSwitcher tone="dark" />
            </div>
            <Link to="/request-access" data-testid="brochure-request-access-link" className="hidden rounded-sm border border-white/15 px-4 py-2 text-sm font-medium text-white/80 transition-colors hover:border-ek-lilac hover:text-ek-lilac sm:inline-block">
              {t("common.requestAccess")}
            </Link>
            {isAuthed ? (
              <Link to="/dashboard" data-testid="brochure-dashboard-link" className="rounded-sm border border-ek-lilac bg-ek-lilac/15 px-4 py-2 text-sm font-medium text-ek-lilac transition-colors hover:bg-ek-lilac hover:text-ek-ink">
                {t("common.goToDashboard")}
              </Link>
            ) : (
              <Link to="/login" data-testid="brochure-signin-link" className="rounded-sm border border-ek-lilac px-4 py-2 text-sm font-medium text-ek-lilac transition-colors hover:bg-ek-lilac hover:text-ek-ink">
                {t("common.signIn")}
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-ek-ink">
        <div className="pointer-events-none absolute -right-32 -top-32 h-[28rem] w-[28rem] rounded-full bg-ek-purple/30 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-40 -left-24 h-[24rem] w-[24rem] rounded-full bg-ek-lilac/20 blur-3xl" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-6 py-20 sm:py-28">
          <p className="ek-eyebrow text-ek-lilac"><Sparkles size={13} className="mr-1 inline" /> {t("howItWorks.heroEyebrow")}</p>
          <h1 className="ek-heading mt-4 max-w-3xl text-4xl text-white sm:text-5xl lg:text-6xl">
            {t("howItWorks.heroTitle")}
          </h1>
          <HeroLivePulse />
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/65">
            {t("howItWorks.heroSubtitle")}
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-3">
            <LeadFormDialog
              kind="demo"
              source="how-it-works/hero"
              trigger={
                <button type="button" data-testid="hero-request-demo" className="ek-btn !bg-ek-lilac !text-ek-ink hover:!bg-white">
                  {t("howItWorks.requestDemo")} <ArrowRight size={16} />
                </button>
              }
            />
            <LeadFormDialog
              kind="brochure"
              source="how-it-works/hero"
              trigger={
                <button type="button" data-testid="brochure-request" className="inline-flex items-center gap-2 rounded-sm border border-white/15 px-5 py-3 text-sm font-medium text-white/80 transition-colors hover:border-ek-lilac hover:text-ek-lilac">
                  <Download size={16} /> {t("howItWorks.requestBrochure")}
                </button>
              }
            />
          </div>
        </div>
      </section>

      {/* Enterprise trust strip */}
      <section className="border-y border-white/10 bg-ek-ink/95" data-testid="trust-strip">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-8 gap-y-3 px-6 py-5">
          {[
            { icon: Scale, label: t("howItWorks.trustDora") },
            { icon: ShieldCheck, label: t("howItWorks.trustPatent") },
            { icon: Lock, label: t("howItWorks.trustSoc2") },
            { icon: Network, label: t("howItWorks.trustMfa") },
          ].map((b) => (
            <span key={b.label} data-testid={`trust-${b.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="inline-flex items-center gap-2 text-sm font-medium text-white/70">
              <b.icon size={15} className="text-ek-lilac" /> {b.label}            </span>
          ))}
        </div>
      </section>

      <LiveIntegrationsStrip />

      {/* Outcomes */}
      <section className="mx-auto max-w-7xl px-6 py-16">
        <h2 className="ek-eyebrow">{t("howItWorks.outcomesEyebrow")}</h2>
        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {OUTCOMES.map((o) => (
            <div key={o.to} className="rounded-md border border-black/5 bg-white p-6">
              <p className="font-display text-lg font-semibold text-ek-ink/65">{o.stat}</p>
              <p className="font-display text-2xl font-bold text-ek-purple">{o.to}</p>
              <p className="mt-3 text-sm leading-relaxed text-ek-ink/70">{o.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Breadth of signals */}
      <section className="bg-white py-16">
        <div className="mx-auto max-w-7xl px-6">
          <h2 className="ek-eyebrow">{t("howItWorks.signalsEyebrow")}</h2>
          <p className="mt-2 max-w-2xl text-base text-ek-ink/70">
            {t("howItWorks.signalsBlurb")}
          </p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {SIGNAL_FAMILIES.map((s) => (
              <div key={s.title} data-testid={`signal-family-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="group rounded-md border border-black/5 bg-ek-canvas p-5 transition-all hover:-translate-y-0.5 hover:shadow-md">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-ek-purple/10 text-ek-purple transition-colors group-hover:bg-ek-purple group-hover:text-white">
                  <s.icon size={20} />
                </span>
                <h3 className="mt-4 font-display text-base font-semibold text-ek-ink">{s.title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-ek-ink/70">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Conceptual flow */}
      <section className="mx-auto max-w-7xl px-6 py-16">
        <h2 className="ek-eyebrow">{t("howItWorks.flowEyebrow")}</h2>
        <p className="mt-2 max-w-2xl text-base text-ek-ink/70">
          {t("howItWorks.flowBlurb")}
        </p>
        <div className="mt-6">
          <Mermaid chart={FLOW} />
        </div>
      </section>

      {/* Catalogue discovery — drives /how-it-works traffic to the catalogue */}
      <section className="mx-auto max-w-7xl px-6 pb-16" data-testid="howitworks-catalogue-tile">
        <div className="overflow-hidden rounded-2xl border border-black/5 bg-gradient-to-br from-ek-purple/5 via-white to-amber-50">
          <div className="flex flex-col items-start gap-6 p-8 md:flex-row md:items-center md:justify-between md:p-10">
            <div className="max-w-2xl">
              <p className="ek-eyebrow text-ek-purple">{t("howItWorks.catalogueEyebrow")}</p>
              <h2 className="mt-2 font-display text-2xl font-semibold text-ek-ink sm:text-3xl">
                {t("howItWorks.catalogueTitle")}
              </h2>
              <p className="mt-3 text-base text-ek-ink/70">
                {t("howItWorks.catalogueBlurb")}
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-3 text-xs">
                <span className="rounded-full bg-emerald-50 px-2.5 py-1 font-semibold text-emerald-700">
                  {t("howItWorks.catalogueFreeChip")}
                </span>
                <span className="rounded-full bg-ek-purple/10 px-2.5 py-1 font-semibold text-ek-purple">
                  {t("howItWorks.cataloguePremiumChip")}
                </span>
                <span className="rounded-full bg-amber-50 px-2.5 py-1 font-semibold text-amber-700">
                  {t("howItWorks.catalogueOwnerChip")}
                </span>
              </div>
            </div>
            <Link
              to="/catalogue"
              className="ek-btn inline-flex items-center gap-2 whitespace-nowrap"
              data-testid="howitworks-catalogue-cta"
            >
              {t("howItWorks.catalogueCta")} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="bg-ek-ink py-16">
        <div className="mx-auto max-w-7xl px-6">
          <h2 className="ek-eyebrow text-ek-lilac">{t("howItWorks.trustEyebrow")}</h2>
          <div className="mt-6 grid gap-5 sm:grid-cols-3">
            {TRUST.map((t) => (
              <div key={t.label} className="rounded-md border border-white/10 bg-white/5 p-6">
                <t.icon size={22} className="text-ek-lilac" />
                <h3 className="mt-4 font-display text-base font-semibold text-white">{t.label}</h3>
                <p className="mt-1 text-sm leading-relaxed text-white/55">{t.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap items-center justify-between gap-4 rounded-md border border-ek-lilac/20 bg-ek-purple/10 px-6 py-5">
            <p className="font-display text-lg font-semibold text-white">{t("howItWorks.bottomCta")}</p>
            <LeadFormDialog
              kind="demo"
              source="how-it-works/bottom"
              trigger={
                <button type="button" data-testid="brochure-cta-bottom" className="ek-btn !bg-ek-lilac !text-ek-ink hover:!bg-white">
                  {t("howItWorks.requestDemo")} <ArrowRight size={16} />
                </button>
              }
            />
          </div>
        </div>
      </section>

      <footer className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-3 px-6 py-8 sm:flex-row sm:items-center">
        <p className="text-xs text-ek-ink/65">© {new Date().getFullYear()} {t("common.footerCopyright")} · <a href="/privacy" data-testid="hiw-privacy-link" className="underline-offset-2 hover:text-ek-purple hover:underline">{t("common.privacy")}</a> · <Link to="/status" data-testid="hiw-status-link" className="underline-offset-2 hover:text-ek-purple hover:underline">{t("common.status")}</Link></p>
        <Link to="/trust#trust-accessibility" aria-label="Accessibility — WCAG 2.1 AA conformance, see Trust Centre"
          className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-2.5 py-1 text-xs font-medium text-ek-ink/70 transition-colors hover:border-ek-purple/40 hover:text-ek-purple"
          data-testid="footer-a11y-badge">
          <Accessibility size={13} /> WCAG 2.1 AA
        </Link>
      </footer>
    </div>
  );
}


/**
 * LiveIntegrationsStrip — buyer-visible credibility moment. Pulls
 * `/api/public/connector-freshness` (unauthenticated, no tenant data leaks)
 * and surfaces "N integrations live · X plans live · last sync N min ago".
 *
 * Quietly disappears if nothing is wired (no embarrassing zeros on
 * marketing pages). Re-fetches every 60s so a long page-dwell still shows
 * fresh numbers.
 */
function LiveIntegrationsStrip() {
  const [data, setData] = useState(null);
  useEffect(() => {
    let mounted = true;
    const fetchOnce = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/public/connector-freshness`,
        );
        if (!res.ok) return;
        const j = await res.json();
        if (mounted) setData(j);
      } catch { /* swallow — strip just hides */ }
    };
    fetchOnce();
    const t = setInterval(fetchOnce, 60_000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  const mins = useMinutesSince(data?.most_recent_sync_at);

  // Nothing wired (e.g. fresh install) — hide the strip rather than render zeros.
  if (!data || !data.most_recent_sync_at || data.total_synced_plans <= 0) return null;

  const ago = formatAgo(mins);

  const namedSlugs = (data.slugs || []).map((s) => SLUG_LABEL[s] || s).join(" · ");

  return (
    <section data-testid="live-integrations-strip"
             className="border-y border-violet-100 bg-gradient-to-r from-violet-50/40 via-white to-violet-50/40">
      <div className="mx-auto flex max-w-7xl flex-col items-center gap-2 px-6 py-5 text-center sm:flex-row sm:justify-between sm:text-left">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex h-2 w-2 animate-pulse rounded-full bg-emerald-500"
                title="Connector heartbeat" />
          <p className="text-sm font-medium text-ek-ink">
            <span data-testid="live-strip-count" className="text-ek-purple">{data.live_integrations}</span> live integration{data.live_integrations === 1 ? "" : "s"}
            {namedSlugs && <span className="ml-1.5 text-ek-ink/70">({namedSlugs})</span>}
            <span className="mx-2 text-ek-ink/30">·</span>
            <span data-testid="live-strip-plans" className="text-ek-purple">{data.total_synced_plans.toLocaleString()}</span> plans synced
            <span className="mx-2 text-ek-ink/30">·</span>
            last refresh <span data-testid="live-strip-ago" className="font-medium text-ek-ink">{ago}</span>
          </p>
        </div>
        <p className="text-xs text-ek-ink/55">Real connector data, polled hourly. Never any customer-specific identifiers.</p>
      </div>
    </section>
  );
}


/**
 * HeroLivePulse — compact "what's live right now" metric line sitting under
 * the hero headline. Pulls the same unauthenticated freshness endpoint as
 * LiveIntegrationsStrip but renders inline (no boxed strip) so it reads as
 * proof inside the hero rather than a separate banner.
 *
 * Silent fallback: renders nothing if nothing is wired so a fresh install
 * never displays embarrassing zeros above the headline copy.
 */
function HeroLivePulse() {
  const [data, setData] = useState(null);
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/public/connector-freshness`,
        );
        if (!res.ok) return;
        const j = await res.json();
        if (mounted) setData(j);
      } catch { /* swallow — pulse just hides */ }
    })();
    return () => { mounted = false; };
  }, []);

  const mins = useMinutesSince(data?.most_recent_sync_at);

  if (!data || !data.most_recent_sync_at || data.total_synced_plans <= 0) return null;

  const ago = formatAgo(mins);

  return (
    <p data-testid="hero-live-pulse"
       className="mt-5 inline-flex flex-wrap items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3.5 py-1.5 text-xs font-medium text-emerald-200">
      <span className="inline-flex h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400"
            aria-hidden="true" />
      <span>What&apos;s live right now:</span>
      <span data-testid="hero-pulse-integrations" className="font-semibold text-white">
        {data.live_integrations}
      </span>
      <span>integration{data.live_integrations === 1 ? "" : "s"}</span>
      <span aria-hidden="true" className="text-emerald-200/40">·</span>
      <span data-testid="hero-pulse-plans" className="font-semibold text-white">
        {data.total_synced_plans.toLocaleString()}
      </span>
      <span>plans synced</span>
      <span aria-hidden="true" className="text-emerald-200/40">·</span>
      <span>last sync <span data-testid="hero-pulse-ago" className="font-semibold text-white">{ago}</span></span>
    </p>
  );
}
